import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Loader2, Eye, Plus, ArrowUpDown, ArrowUp, ArrowDown,
  BarChart3, Calendar, Activity, CheckCircle2, AlertTriangle,
  ArrowLeft, ChevronRight, Upload, FileText, Play, Shield,
} from 'lucide-react';
import StepIndicator from '../onboarding/StepIndicator';
import { udqApi } from '../../services/api';
import { formatDate, formatDateTime, scoreBg, gradeColor } from '../../utils/format';
import PaginationBar from '../../components/PaginationBar';
import type { UDQAssessmentHistory, Pagination, UDQReport, UDQDimensionScore } from '../../types';

type SortDir = 'asc' | 'desc';

interface UDQLandingProps {
  onNewAssessment: () => void;
}

export default function UDQLanding({ onNewAssessment }: UDQLandingProps) {
  const [items, setItems] = useState<UDQAssessmentHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({ count: 0, page: 1, per_page: 10, pages: 1 });
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // View mode: null = list, string = viewing a run's full flow
  const [viewId, setViewId] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await udqApi.history({
        page: pagination.page,
        items_per_page: pagination.per_page,
        search,
        sort_col: sortCol,
        sort_dir: sortDir,
      });
      const data = resp.data.payload;
      setItems(data.items);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to load UDQ history', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search, sortCol, sortDir]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPagination(p => ({ ...p, page: 1 }));
  };

  // If viewing a run, show the full flow
  if (viewId) {
    return <UDQFlowView assessmentId={viewId} onBack={() => setViewId(null)} onNewAssessment={onNewAssessment} />;
  }

  // Summary stats
  const totalAssessments = pagination.count;
  const avgScore = items.length > 0
    ? items.reduce((sum, r) => sum + (r.dqs || 0), 0) / items.length
    : 0;
  const lastAssessment = items.length > 0 ? items[0]?.timestamp : null;

  function SortIcon({ col }: { col: string }) {
    if (sortCol !== col) return <ArrowUpDown className="w-3.5 h-3.5 text-cargo-muted/40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-cargo-accent" />
      : <ArrowDown className="w-3.5 h-3.5 text-cargo-accent" />;
  }

  return (
    <div className="space-y-6">
      <button onClick={onNewAssessment}
        className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-cargo-surface border border-cargo-border hover:border-cargo-accent/50 hover:bg-cargo-accent/5 transition-all group">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cargo-accent/15 flex items-center justify-center">
            <Plus className="w-5 h-5 text-cargo-accent" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-cargo-text">New UDQ Assessment</p>
            <p className="text-xs text-cargo-muted">Upload a document and run unstructured data quality analysis</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-cargo-muted group-hover:text-cargo-accent transition-colors" />
      </button>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard icon={<Activity className="w-5 h-5 text-cargo-accent" />} label="Total Assessments" value={String(totalAssessments)} />
        <SummaryCard icon={<BarChart3 className="w-5 h-5 text-blue-400" />} label="Average DQ Score" value={avgScore > 0 ? `${avgScore.toFixed(1)}` : '—'} />
        <SummaryCard icon={<Calendar className="w-5 h-5 text-emerald-400" />} label="Last Assessment" value={lastAssessment ? formatDate(lastAssessment) : '—'} />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            placeholder="Search documents, types..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm placeholder:text-cargo-muted outline-none focus:border-cargo-accent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cargo-border">
                {[
                  { col: 'source_name', label: 'Document' },
                  { col: 'document_type', label: 'Type' },
                  { col: 'industry', label: 'Domain' },
                  { col: 'dqs', label: 'DQ Score' },
                  { col: 'grade', label: 'Grade' },
                  { col: 'ai_safe', label: 'AI Safe' },
                  { col: 'timestamp', label: 'Date' },
                ].map(({ col, label }) => (
                  <th key={col}
                    className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider cursor-pointer hover:text-cargo-text"
                    onClick={() => handleSort(col)}
                  >
                    <span className="flex items-center gap-1">{label} <SortIcon col={col} /></span>
                  </th>
                ))}
                <th className="px-4 py-3 text-xs font-medium text-cargo-muted uppercase tracking-wider">Issues</th>
                <th className="px-4 py-3 text-xs font-medium text-cargo-muted uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cargo-border">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-cargo-accent mx-auto" />
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-cargo-muted">
                  No UDQ assessments found. Click "+ New Assessment" to get started.
                </td></tr>
              ) : items.map((item) => (
                <tr key={item._id} className="hover:bg-cargo-hover/30 transition-colors">
                  <td className="px-4 py-3 text-cargo-text font-medium truncate max-w-[200px]">{item.source_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded bg-cargo-panel text-cargo-muted border border-cargo-border uppercase">
                      {(item.document_type || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-cargo-muted capitalize">{item.industry}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${scoreBg(item.dqs)}`}>
                      {(item.dqs || 0).toFixed(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-bold ${gradeColor(item.grade)}`}>{item.grade || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    {item.ai_safe ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Safe
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-red-400">
                        <AlertTriangle className="w-3.5 h-3.5" /> Unsafe
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-cargo-muted">{formatDate(item.timestamp)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5 text-[10px]">
                      {item.issues_summary?.critical > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">{item.issues_summary.critical}C</span>
                      )}
                      {item.issues_summary?.high > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">{item.issues_summary.high}H</span>
                      )}
                      {item.issues_summary?.medium > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">{item.issues_summary.medium}M</span>
                      )}
                      {item.issues_summary?.low > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">{item.issues_summary.low}L</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setViewId(item._id)}
                      className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors"
                      title="View full flow"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && items.length > 0 && (
          <PaginationBar
            page={pagination.page}
            totalPages={pagination.pages}
            perPage={pagination.per_page}
            onPageChange={(p) => setPagination(prev => ({ ...prev, page: p }))}
            onPerPageChange={(n) => setPagination(prev => ({ ...prev, per_page: n, page: 1 }))}
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   UDQ Full Flow Read-Only View
   ═══════════════════════════════════════ */

const FLOW_STEPS = ['Upload', 'Run Quality Test', 'Report Results', 'AI Ready DQ Score'];

function UDQFlowView({ assessmentId, onBack, onNewAssessment }: { assessmentId: string; onBack: () => void; onNewAssessment: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    setLoading(true);
    udqApi.historyDetail(assessmentId)
      .then((resp) => { setData(resp.data.payload); setStep(3); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [assessmentId]);

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-cargo-accent" /></div>;
  }
  if (!data) {
    return <div className="text-center py-24 text-cargo-muted">Failed to load assessment data.</div>;
  }

  const report: UDQReport | null = data.full_report || null;
  const composite = report?.composite;
  const processing = report?.processing;
  const dims: UDQDimensionScore[] = report?.dimensions
    ? Object.values(report.dimensions).sort((a, b) => b.score - a.score)
    : [];

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-cargo-text">UDQ Assessment — {data.source_name}</h1>
          <p className="text-cargo-muted text-sm">{formatDateTime(data.timestamp)}</p>
        </div>
      </div>

      <StepIndicator steps={FLOW_STEPS} current={step} />

      <div className="mt-8">
        {/* Step 0: Upload */}
        {step === 0 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6">
            <h2 className="text-lg font-semibold text-cargo-text mb-4">Document Upload</h2>
            <div className="flex items-center gap-5 mb-6">
              <div className="w-16 h-16 rounded-xl bg-cargo-accent/10 flex items-center justify-center">
                <Upload className="w-8 h-8 text-cargo-accent" />
              </div>
              <div>
                <p className="text-lg font-bold text-cargo-text">{data.source_name}</p>
                <p className="text-sm text-cargo-muted">Uploaded document for quality assessment</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <InfoCard icon={<FileText className="w-5 h-5 text-cargo-accent" />} label="Document" value={data.source_name || '—'} />
              <InfoCard icon={<FileText className="w-5 h-5 text-purple-400" />} label="Source Type" value={(data.source_type || '—').toUpperCase()} />
              <InfoCard
                icon={<FileText className="w-5 h-5 text-cargo-warning" />}
                label="Document Type"
                value={(data.document_type || '—').replace(/_/g, ' ')}
              />
              <InfoCard icon={<Shield className="w-5 h-5 text-emerald-400" />} label="Industry" value={data.industry || '—'} />
            </div>
          </div>
        )}

        {/* Step 1: Run Quality Test */}
        {step === 1 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6 space-y-6">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto bg-emerald-500/10 rounded-xl flex items-center justify-center mb-3">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
              <p className="text-cargo-text font-medium">Quality Test Completed</p>
              <p className="text-cargo-muted text-sm mt-1">
                Document was processed and analyzed for data quality dimensions.
              </p>
            </div>

            {processing && (
              <div>
                <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-3">Processing Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard label="Pages" value={String(processing.page_count ?? '—')} />
                  <MetricCard label="Text Length" value={processing.text_length?.toLocaleString() ?? '—'} />
                  <MetricCard label="Entities Found" value={String(processing.entities_found ?? '—')} />
                  <MetricCard label="OCR Confidence" value={processing.ocr_confidence_avg != null ? `${processing.ocr_confidence_avg}%` : '—'} />
                </div>
              </div>
            )}

            {report && (
              <div>
                <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-3">Report Metadata</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard label="Report ID" value={report.report_id || '—'} />
                  <MetricCard label="Module" value={(report.module || '—').toUpperCase()} />
                  <MetricCard label="Source Type" value={(report.source_type || '—').toUpperCase()} />
                  <MetricCard label="Timestamp" value={report.timestamp ? new Date(report.timestamp).toLocaleString() : '—'} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Report Results — Dimension Breakdown */}
        {step === 2 && (
          <div className="space-y-6">
            {dims.length > 0 ? (
              <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
                <h3 className="text-sm font-semibold text-cargo-text mb-4">Dimension Breakdown ({dims.length} dimensions)</h3>
                <div className="space-y-2">
                  {dims.map((dim) => {
                    const name = dim.dimension.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                    const s = dim.score || 0;
                    return (
                      <div key={dim.dimension} className="flex items-center gap-3 py-1">
                        <span className="w-40 shrink-0 text-xs text-cargo-muted">{name}</span>
                        <div className="flex-1 h-2 bg-cargo-border rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${s >= 90 ? 'bg-emerald-500' : s >= 75 ? 'bg-blue-500' : s >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${s}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-cargo-text w-8 text-right">{s.toFixed(0)}</span>
                        <span className={`text-xs font-bold w-4 ${
                          dim.grade === 'A' ? 'text-emerald-400' : dim.grade === 'B' ? 'text-blue-400' :
                          dim.grade === 'C' ? 'text-yellow-400' : dim.grade === 'D' ? 'text-orange-400' : 'text-red-400'
                        }`}>{dim.grade}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-cargo-surface rounded-xl border border-cargo-border p-8 text-center text-cargo-muted">
                No dimension data available for this assessment.
              </div>
            )}

            {/* Issues Summary */}
            {report?.issues_summary && (
              <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
                <h3 className="text-sm font-semibold text-cargo-text mb-3">Issues Summary</h3>
                <div className="flex gap-3 flex-wrap">
                  {(['critical', 'high', 'medium', 'low'] as const).map((sev) => (
                    <span key={sev} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                      sev === 'critical' || sev === 'high' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                      sev === 'medium' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                      'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    }`}>{sev}: {(report.issues_summary as any)[sev] || 0}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: AI Ready DQ Score */}
        {step === 3 && (
          <ScoreView composite={composite} data={data} report={report} dims={dims} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button onClick={() => step > 0 ? setStep(step - 1) : onBack()}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-cargo-border text-cargo-muted hover:text-cargo-text transition-colors">
          <ArrowLeft className="w-4 h-4" /> {step === 0 ? 'Back to History' : 'Back'}
        </button>
        {step < 3 && (
          <button onClick={() => setStep(step + 1)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cargo-accent hover:bg-cargo-accent-hover text-white font-medium transition-colors">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <button onClick={onNewAssessment}
        className="w-full flex items-center justify-between mt-6 px-5 py-4 rounded-xl bg-cargo-surface border border-cargo-border hover:border-cargo-accent/50 hover:bg-cargo-accent/5 transition-all group">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cargo-accent/15 flex items-center justify-center">
            <Plus className="w-5 h-5 text-cargo-accent" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-cargo-text">New UDQ Assessment</p>
            <p className="text-xs text-cargo-muted">Start a new unstructured data quality analysis</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-cargo-muted group-hover:text-cargo-accent transition-colors" />
      </button>
    </div>
  );
}

/* ── Score View (step 3) ── */

function ScoreView({ composite, data, report, dims }: { composite: any; data: any; report: UDQReport | null; dims: UDQDimensionScore[] }) {
  const circumference = 2 * Math.PI * 70;
  const score = composite?.dqs || data?.dqs || 0;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="space-y-6">
      {/* Score Ring + Grade */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-8 text-center">
        <div className="relative w-40 h-40 mx-auto mb-4">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="70" fill="none" stroke="currentColor" className="text-cargo-border" strokeWidth="10" />
            <circle
              cx="80" cy="80" r="70" fill="none" strokeWidth="10" strokeLinecap="round"
              className={score >= 90 ? 'text-emerald-500' : score >= 75 ? 'text-blue-500' : score >= 60 ? 'text-yellow-500' : 'text-red-500'}
              stroke="currentColor"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-cargo-text">{score.toFixed(1)}</span>
            <span className="text-xs text-cargo-muted">/ 100</span>
          </div>
        </div>

        {composite && (
          <>
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold border-2 ${
                composite.grade === 'A' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' :
                composite.grade === 'B' ? 'border-blue-500 text-blue-400 bg-blue-500/10' :
                composite.grade === 'C' ? 'border-yellow-500 text-yellow-400 bg-yellow-500/10' :
                composite.grade === 'D' ? 'border-orange-500 text-orange-400 bg-orange-500/10' :
                'border-red-500 text-red-400 bg-red-500/10'
              }`}>
                {composite.grade}
              </div>
              <div className="text-left">
                <p className="text-base font-semibold text-cargo-text">{composite.label}</p>
                <p className="text-xs text-cargo-muted">{composite.action?.replace(/_/g, ' ')}</p>
              </div>
            </div>

            {/* AI Safe badge */}
            {composite.ai_safe ? (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                <CheckCircle2 className="w-3.5 h-3.5" /> AI Safe
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30">
                <AlertTriangle className="w-3.5 h-3.5" /> Not AI Safe
              </span>
            )}
          </>
        )}
      </div>

      {/* Dimensions quick overview */}
      {dims.length > 0 && (
        <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
          <h3 className="text-sm font-semibold text-cargo-text mb-4">Dimension Scores</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {dims.map((dim) => {
              const name = dim.dimension.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
              const s = dim.score || 0;
              return (
                <div key={dim.dimension} className="bg-cargo-panel rounded-lg p-3 text-center">
                  <p className="text-xs text-cargo-muted truncate mb-1">{name}</p>
                  <p className={`text-lg font-bold ${
                    s >= 90 ? 'text-emerald-400' : s >= 75 ? 'text-blue-400' : s >= 60 ? 'text-yellow-400' : 'text-red-400'
                  }`}>{s.toFixed(0)}</p>
                  <p className={`text-xs font-bold ${
                    dim.grade === 'A' ? 'text-emerald-400' : dim.grade === 'B' ? 'text-blue-400' :
                    dim.grade === 'C' ? 'text-yellow-400' : dim.grade === 'D' ? 'text-orange-400' : 'text-red-400'
                  }`}>{dim.grade}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Issues */}
      {report?.issues_summary && (
        <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
          <h3 className="text-sm font-semibold text-cargo-text mb-3">Issues Summary</h3>
          <div className="flex gap-3 flex-wrap">
            {(['critical', 'high', 'medium', 'low'] as const).map((sev) => (
              <span key={sev} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                sev === 'critical' || sev === 'high' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                sev === 'medium' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                'bg-blue-500/20 text-blue-400 border-blue-500/30'
              }`}>{sev}: {(report.issues_summary as any)[sev] || 0}</span>
            ))}
          </div>
        </div>
      )}

      {/* Processing metadata */}
      {report?.processing && (
        <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
          <h3 className="text-sm font-semibold text-cargo-text mb-3">Processing</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div><p className="text-cargo-muted">Pages</p><p className="text-cargo-text font-medium mt-0.5">{report.processing.page_count}</p></div>
            <div><p className="text-cargo-muted">Text Length</p><p className="text-cargo-text font-medium mt-0.5">{report.processing.text_length.toLocaleString()}</p></div>
            <div><p className="text-cargo-muted">Entities</p><p className="text-cargo-text font-medium mt-0.5">{report.processing.entities_found}</p></div>
            <div><p className="text-cargo-muted">OCR Confidence</p><p className="text-cargo-text font-medium mt-0.5">{report.processing.ocr_confidence_avg}%</p></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Shared Components ── */

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-cargo-panel flex items-center justify-center">{icon}</div>
      <div>
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-lg font-bold text-cargo-text">{value}</p>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 bg-cargo-panel rounded-lg p-3">
      {icon}
      <div>
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-sm font-medium text-cargo-text truncate capitalize">{value}</p>
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-cargo-panel rounded-lg p-3">
      <p className="text-xs text-cargo-muted">{label}</p>
      <p className="text-sm font-medium text-cargo-text mt-0.5">{value}</p>
    </div>
  );
}
